import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * ### 入所募集問い合わせ API 操作クラス
 */
export default class RecruitmentContacts {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET RecruitmentContact
   */
  public async get(userStore: LoginUser, recruitmentContactsSortKey: string): Promise<RecruitmentContactsGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      recruitmentContactsSortKey: recruitmentContactsSortKey
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.RecruitmentContact, formatParams);
    const response = await this.commonService.executeGet<RecruitmentContactsGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET RecruitmentContacts
   */
  public async gets(userStore: LoginUser): Promise<Array<RecruitmentContactsGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.RecruitmentContacts, formatParams);
    const response = await this.commonService.executeGet<Array<RecruitmentContactsGetModel>>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.IGNORE, 0);

    return response.data;
  }

  /**
   * ### POST RecruitmentContact
   */
  public async post(userStore: LoginUser, requestBody: RecruitmentContactsPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.RecruitmentContacts, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### PATCH RecruitmentContact
   */
  public async patch(userStore: LoginUser, recruitmentContactsSortKey: string, requestBody: RecruitmentContactsPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      recruitmentContactsSortKey: recruitmentContactsSortKey
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.RecruitmentContact, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### DELETE RecruitmentContact
   */
  public async delete(userStore: LoginUser, recruitmentContactsSortKey: string): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      recruitmentContactsSortKey: recruitmentContactsSortKey
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.RecruitmentContact, formatParams);
    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface RecruitmentContactsGetModel {
  /** 施設を示す ID */
  facilityId: number;
  /** ソートキー */
  recruitmentContactsSortKey: string;

  /** 問い合わせ日時 */
  contactDatetime: number;
  /** 問い合わせ種別 */
  contactType: ContactType;
  /** 対応済みか否か */
  done: boolean;
  /** 最後に対応済みにした日時 */
  latestDoneDatetime: number | null;
  /** 最後に対応済みにしたユーザー ID */
  latestDoneUserId: string | null;
  /** 最後に対応済みにしたユーザー名 */
  latestDoneUserName: string | null;
  /** 氏名 */
  name: string;
  /** 電話番号 */
  tel: string;
  /** 問い合わせ内容 */
  contactMessage: string | null;
  /** 施設の見学予約に対する担当者 */
  customerName: string | null;
  /** 対応メモ */
  supportMemo: string | null;

  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 最終更新日 */
  updated: number;
  /** 作成した日付(YYYYMMDDhhmmssSSS) */
  created: number;
}

export enum ContactType {
  /** 本人 */
  USER = "user",
  /** 施設ユーザー */
  FACILITY = "facility"
}

export interface RecruitmentContactsPostModel {
  /** 問い合わせ日時 */
  contactDatetime: number;
  /** 問い合わせ種別 */
  contactType: ContactType;
  /** 対応済みか否か */
  done: boolean;
  /** 氏名 */
  name: string;
  /** 電話番号 */
  tel: string;
  /** 問い合わせ内容 */
  contactMessage: string | null;
  /** 施設の見学予約に対する担当者 */
  customerName: string | null;
  /** 対応メモ */
  supportMemo: string | null;
}

export interface RecruitmentContactsPatchModel {
  /** 対応済みか否か */
  done: boolean;
  /** 施設の見学予約に対する担当者 */
  customerName: string | null;
  /** 対応メモ */
  supportMemo: string | null;
  /** 更新日時 */
  updated: number;
}
